// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { arrForEach, arrIndexOf, createCachedValue, isArray, isFunction, isNullOrUndefined, isString, objDefineProps, safeGetDeferred, strSplit } from "@nevware21/ts-utils";
import { _DYN_JOIN, _DYN_LENGTH, _DYN_PUSH, _DYN_SPLICE, _DYN_VALUE } from "../__DynamicConstants";
import { STR_EMPTY } from "../constants/InternalConstants";
import { findMetaTags, findNamedServerTimings } from "../utils/EnvUtils";
import { setObjStringTag } from "../utils/HelperFuncs";
var MAX_TRACE_STATE_MEMBERS = 32;
var MAX_TRACE_STATE_LEN = 512;
// https://www.w3.org/TR/trace-context-1/#key
var LCALPHA = "[a-z]";
var LCALPHA_DIGIT = "[a-z\\d]";
var LCALPHA_DIGIT_UNDERSCORE_DASH_STAR_SLASH = "[a-z\\d_\\-*\\/]";
var SIMPLE_KEY = "(" + LCALPHA + LCALPHA_DIGIT_UNDERSCORE_DASH_STAR_SLASH + "{0,255})";
var TENANT_ID = "(" + LCALPHA_DIGIT + LCALPHA_DIGIT_UNDERSCORE_DASH_STAR_SLASH + "{0,240})";
var SYSTEM_ID = "(" + LCALPHA + LCALPHA_DIGIT_UNDERSCORE_DASH_STAR_SLASH + "{0,13})";
var MULTI_TENANT_KEY = "(" + TENANT_ID + "@" + SYSTEM_ID + ")";
// https://www.w3.org/TR/trace-context-1/#value
var NBLK_CHAR = "\x21-\x2B\\--\x3C\x3E-\x7E";
var TRACESTATE_VALUE = "[\x20" + NBLK_CHAR + "]{0,255}[" + NBLK_CHAR + "]";
// https://www.w3.org/TR/trace-context-1/#tracestate-header
var TRACESTATE_KVP_REGEX = ( /*#__PURE__*/new RegExp("^\\s*((?:" + SIMPLE_KEY + "|" + MULTI_TENANT_KEY + ")=(" + TRACESTATE_VALUE + "))\\s*$"));
/**
 * @internal
 * Parse a trace state key/value pair
 * @param value - the key/value pair as a string
 * @returns The trace state member if valid, otherwise null
 */
function _parseListMember(value) {
    if (value) {
        TRACESTATE_KVP_REGEX.lastIndex = 0; // Reset the regex to ensure we start from the beginning
        var match = TRACESTATE_KVP_REGEX.exec(value);
        if (match && match[_DYN_LENGTH /* @min:%2elength */] >= 7 && match[1] && match[6]) {
            var type = match[3] ? 1 /* eTraceStateKeyType.multiTenant */ : 0 /* eTraceStateKeyType.simple */;
            var multiTenant = null;
            if (type === 1 /* eTraceStateKeyType.multiTenant */) {
                multiTenant = {
                    tenantId: match[4],
                    systemId: match[5]
                };
            }
            var parts = {
                type: type,
                key: match[2],
                multiTenant: multiTenant,
                value: match[6]
            };
            return parts;
        }
    }
    return null;
}
/**
 * @internal
 * Parse the trace state list from a string
 * @param value - the list of trace states as a string
 * @returns An array of trace state members
 */
function _parseTraceStateList(value) {
    var items = [];
    if (value) {
        var addedKeys_1 = [];
        arrForEach(strSplit(value, ","), function (member) {
            var parts = _parseListMember(member);
            if (parts) {
                // As per the spec, the first occurrence of a key is the one that should be used
                // as all new entries are added to the front (left) of the list
                if (arrIndexOf(addedKeys_1, parts.key) === -1) {
                    items[_DYN_PUSH /* @min:%2epush */](parts);
                    addedKeys_1[_DYN_PUSH /* @min:%2epush */](parts.key);
                    if (items[_DYN_LENGTH /* @min:%2elength */] >= MAX_TRACE_STATE_MEMBERS) {
                        // The trace state list should not exceed 32 members
                        return -1;
                    }
                }
            }
        });
    }
    return items;
}
function _indexOf(items, key) {
    for (var lp = 0; lp < items[_DYN_LENGTH /* @min:%2elength */]; lp++) {
        if (items[lp].key === key) {
            return lp;
        }
    }
    return -1;
}
function _keys(items, parent) {
    var keys = [];
    var delKeys = [];
    arrForEach(items, function (member) {
        if (member[_DYN_VALUE /* @min:%2evalue */] != null) {
            keys[_DYN_PUSH /* @min:%2epush */](member.key);
        }
        else {
            delKeys[_DYN_PUSH /* @min:%2epush */](member.key);
        }
    });
    if (parent) {
        // Get and add parent keys that are not in the current list or marked as deleted
        arrForEach(parent.keys, function (key) {
            if (arrIndexOf(keys, key) === -1 && arrIndexOf(delKeys, key) === -1) {
                keys[_DYN_PUSH /* @min:%2epush */](key);
            }
        });
    }
    return keys;
}
/**
 * @internal
 * Identifies if the provided items are empty, meaning it has no keys or values.
 * @param items - The items to check
 * @param parent - The parent trace state to check for keys
 * @returns true if the items are empty, false otherwise
 */
/*#__NO_SIDE_EFFECTS__*/
function _isEmpty(items, parent) {
    var delKeys;
    var isEmpty = true;
    if (items && items[_DYN_LENGTH /* @min:%2elength */] > 0) {
        arrForEach(items, function (member) {
            if (member[_DYN_VALUE /* @min:%2evalue */] != null) {
                isEmpty = false;
            }
            else {
                if (!delKeys) {
                    delKeys = [];
                }
                // If the value is null then this is a deleted key, so we can ignore it
                delKeys[_DYN_PUSH /* @min:%2epush */](member.key);
            }
        });
    }
    if (isEmpty && parent) {
        isEmpty = parent.isEmpty;
        if (!isEmpty && delKeys && delKeys[_DYN_LENGTH /* @min:%2elength */] > 0) {
            // If the parent is not empty then we need to check if any of the keys are in the deleted list
            isEmpty = true;
            arrForEach(parent.keys, function (key) {
                if (arrIndexOf(delKeys, key) === -1) {
                    isEmpty = false;
                    return -1; // Break out of the loop
                }
            });
        }
    }
    return isEmpty;
}
/**
 * Identifies if the provided value looks like a distributed trace state instance
 * @param value - The value to check
 * @returns - True if the value looks like a distributed trace state instance
 */
/*#__NO_SIDE_EFFECTS__*/
export function isW3cTraceState(value) {
    return !!(value && isArray(value.keys) && isFunction(value.get) && isFunction(value.set) && isFunction(value.del) && isFunction(value.hdrs));
}
/**
 * Creates a new mutable {@link IW3cTraceState} instance, optionally inheriting from the parent trace state
 * and optionally using the provided encoded string value as the initial trace state.
 * Calls to {@link IW3cTraceState.set} and {@link IW3cTraceState.del} will modify the current instance
 * which means that any child instance that is using this instance as a parent will also be indirectly
 * modified unless the child instance has overridden the value associated with the modified key.
 * @since 3.4.0
 * @param value - The string value for the trace state
 * @param parent - The parent trace state to inherit any existing keys from.
 * @returns - A new distributed trace state instance
 */
/*#__NO_SIDE_EFFECTS__*/
export function createW3cTraceState(value, parent) {
    var cachedItems = safeGetDeferred(_parseTraceStateList, [], [value || STR_EMPTY]);
    function _get(key) {
        var value;
        var theItems = cachedItems.v;
        var idx = _indexOf(theItems, key);
        if (idx !== -1) {
            var itmValue = theItems[idx][_DYN_VALUE /* @min:%2evalue */];
            if (itmValue != null) {
                // Special case for the value being null, which means the key was deleted
                value = itmValue;
            }
        }
        else if (parent) {
            // Get the value from the parent if it exists
            value = parent.get(key);
        }
        return value;
    }
    function _setMember(member) {
        if (member) {
            var theItems = cachedItems.v;
            var idx = _indexOf(theItems, member.key);
            if (idx !== -1) {
                // Move the item to the front of the list, removing the previous instance
                theItems[_DYN_SPLICE /* @min:%2esplice */](idx, 1);
            }
            theItems.unshift(member);
            // We need to re-create the cached value as during testing the cached lazy value
            // may get re-evaluated resetting the items to the original value
            cachedItems = createCachedValue(theItems);
            return 0;
        }
        return -1;
    }
    function _set(key, value) {
        var member;
        if (key && isString(key) && !isNullOrUndefined(value) && isString(value)) {
            member = _parseListMember(key + "=" + value); // Validate the key/value pair before adding it to the state
        }
        return _setMember(member);
    }
    function _del(key) {
        _setMember({
            type: 2 /* eTraceStateKeyType.deleted */,
            key: key
        });
    }
    function _headers(maxHeaders, maxKeys, maxLen) {
        var results = [];
        var result = STR_EMPTY;
        var numKeys = 0;
        var len = 0;
        // Default to the max values if not provided
        maxKeys = maxKeys || MAX_TRACE_STATE_MEMBERS;
        // Default to the max length if not provided
        maxLen = maxLen || MAX_TRACE_STATE_LEN;
        var theItems = cachedItems.v;
        arrForEach(_keys(theItems, parent), function (key) {
            var value = _get(key);
            if (!isNullOrUndefined(value) && isString(value)) {
                numKeys++;
                var val = key + "=" + value;
                var valLen = val[_DYN_LENGTH /* @min:%2elength */];
                if (len + 1 + valLen >= maxLen) {
                    // Don't exceed the max length for any single combined header value
                    results[_DYN_PUSH /* @min:%2epush */](result);
                    if (maxHeaders && results[_DYN_LENGTH /* @min:%2elength */] <= maxHeaders) {
                        // Don't exceed the max number of entries
                        return -1;
                    }
                    result = STR_EMPTY;
                    len = 0;
                }
                if (result[_DYN_LENGTH /* @min:%2elength */] > 0) {
                    result += ",";
                    len++;
                }
                result += val;
                len += valLen;
                if (numKeys >= maxKeys) {
                    // Only allow the first maxKeys members
                    return -1;
                }
            }
        });
        if (result) {
            results[_DYN_PUSH /* @min:%2epush */](result);
        }
        return results;
    }
    var traceStateList = {
        keys: [],
        isEmpty: false,
        get: _get,
        set: _set,
        del: _del,
        hdrs: _headers,
        child: function () { return createW3cTraceState(null, traceStateList); }
    };
    function _toString() {
        var headers = traceStateList.hdrs(1);
        return headers[_DYN_LENGTH /* @min:%2elength */] > 0 ? headers[0] : STR_EMPTY;
    }
    objDefineProps(traceStateList, {
        "keys": {
            g: function () { return _keys(cachedItems.v, parent); }
        },
        "isEmpty": {
            g: function () { return _isEmpty(cachedItems.v, parent); }
        },
        "toString": {
            v: _toString,
            e: false // Do not allow the toString to be enumerated
        },
        "_p": {
            v: parent,
            e: false // Do not allow the parent to be enumerated
        }
    });
    setObjStringTag(traceStateList, _toString);
    return traceStateList;
}
/**
 * Create a new independent instance of IW3cTraceState that contains a snapshot of all current key/value pairs
 * from the provided instance and any parent instances. The returned instance will have no parent and will be completely
 * independent from any future changes to the original instance or its parent chain.
 * This is useful when you need to capture the current state and ensure it remains unchanged regardless of
 * future modifications to the parent instances.
 * @since 3.4.0
 * @param traceState - The trace state instance to snapshot
 * @returns A new independent instance of IW3cTraceState with all current key/value pairs captured
 */
/*#__NO_SIDE_EFFECTS__*/
export function snapshotW3cTraceState(traceState) {
    // Create a new independent instance with no parent
    // This ensures the returned instance is completely independent from future changes
    var snapshot = createW3cTraceState(null, null);
    if (traceState) {
        var theKeys = traceState.keys;
        // Iterate over the keys in reverse order to maintain correct precedence
        // Since set() adds items to the front, we need to add them in reverse order
        // to preserve the original key ordering where newer keys take precedence
        for (var i = theKeys[_DYN_LENGTH /* @min:%2elength */] - 1; i >= 0; i--) {
            var key = theKeys[i];
            var value = traceState.get(key);
            if (!isNullOrUndefined(value) && isString(value)) {
                // Use the set function to add the key/value pair to the snapshot
                // This leverages the existing validation and formatting logic
                snapshot.set(key, value);
            }
        }
    }
    return snapshot;
}
/**
 * Helper function to fetch the passed traceparent from the page, looking for it as a meta-tag or a Server-Timing header.
 * @since 3.4.0
 * @param selectIdx - If the found value is comma separated which is the preferred entry to select, defaults to the first
 * @returns
 */
/*#__NO_SIDE_EFFECTS__*/
export function findW3cTraceState() {
    var name = "tracestate";
    var traceState = null;
    var metaTags = findMetaTags(name);
    if (metaTags[_DYN_LENGTH /* @min:%2elength */] > 0) {
        traceState = createW3cTraceState(metaTags[_DYN_JOIN /* @min:%2ejoin */](","));
    }
    if (!traceState) {
        var serverTimings = findNamedServerTimings(name);
        if (serverTimings[_DYN_LENGTH /* @min:%2elength */] > 0) {
            traceState = createW3cTraceState(serverTimings[_DYN_JOIN /* @min:%2ejoin */](","));
        }
    }
    return traceState;
}
//# sourceMappingURL=W3cTraceState.js.map